/**
 * External Imports
 */
import { FC, useEffect, useState, Dispatch, SetStateAction } from 'react';
import { connect, useDispatch } from 'react-redux';

/**
 * Internal Imports
 */
import SideNav from '../../components/sideNav/adminSideNav/usersListSideNav';
import SearchHeader from '../../components/searchHeader';
import UsersTable from '../../components/customTables/admin/usersTable';

import { RootState } from '../../redux/store';
import {
  clearAdminUserFilter,
  getCSVToExportSuccess
} from '../../redux/actions';
import {
  useLazyGetUsersQuery,
  useLazyGetUsersForDownloadQuery
} from '../../redux/apis/Admin.api';

import { useDebounceValue } from '../../utils/debouncers';
import { isHca34 } from '../../utils/helpers';

/**
 * Global Type Definition Imports
 */
import { Filter } from '../../config/interfaces';

/**
 * Style Imports
 */
import { UsersListContainer } from './styles/AdminViews.styles';

interface UsersListPageProps {
  adminUserFilter: Filter;
  currentUsersPage: number;
  searchTerm: string;
  setSearchTerm: Dispatch<SetStateAction<string>>;
}

const UsersList: FC<UsersListPageProps> = ({
  adminUserFilter,
  currentUsersPage,
  searchTerm,
  setSearchTerm
}) => {
  const dispatch = useDispatch();

  const [selectedTab, setSelectedTab] = useState<string>('allUsers');
  // const { pathname } = useLocation();

  // debouncing search terms
  const debouncedUserSearchTerm = useDebounceValue(searchTerm, 275);

  const [
    getUsersQuery,
    {
      data: usersData,
      // isError: isUsersError,
      // isFetching: isUsersFetching,
      isLoading: isUsersLoading
    }
  ] = useLazyGetUsersQuery();
  const [
    getUsersForDownload
    // {
    //   data: usersForDownloadData,
    //   isError: isUsersForDownloadError,
    //   isFetching: isUsersForDownloadFetching,
    //   isLoading: isUsersForDownloadLoading
    // }
  ] = useLazyGetUsersForDownloadQuery();

  const handleExportCsv = async () => {
    // get all the pages with selected criteria
    const { data, error } = await getUsersForDownload({
      page: String(-1),
      role:
        selectedTab === 'allUsers'
          ? adminUserFilter?.userRole || ''
          : selectedTab,
      name: !isHca34(searchTerm) ? searchTerm.trim() : '',
      hcaId: isHca34(searchTerm) ? searchTerm.toLowerCase().trim() : '',
      accessLevel: adminUserFilter?.accessLevel || '',
      accessLevelId: adminUserFilter?.accessPlace || ''
    });
    if (error) {
      console.log(error);
    } else {
      dispatch(getCSVToExportSuccess([...data]));
    }
  };

  useEffect(() => {
    return () => {
      dispatch(clearAdminUserFilter());
    };
  }, []);

  useEffect(() => {
    // clear search when tab changes
    setSearchTerm('');
  }, [selectedTab]);

  useEffect(() => {
    getUsersQuery({
      page: String(currentUsersPage - 1), // backend is 0 indexed
      role:
        selectedTab === 'allUsers'
          ? adminUserFilter?.userRole || ''
          : selectedTab,
      accessLevel: adminUserFilter?.accessLevel || '',
      accessLevelId: adminUserFilter?.accessPlace || '',
      name: !isHca34(debouncedUserSearchTerm)
        ? debouncedUserSearchTerm.trim()
        : '',
      hcaId: isHca34(debouncedUserSearchTerm)
        ? debouncedUserSearchTerm.toLowerCase().trim()
        : ''
    });
  }, [currentUsersPage, selectedTab, debouncedUserSearchTerm, adminUserFilter]);

  return (
    <UsersListContainer id="Admin-User-List">
      <SideNav selectedTab={selectedTab} setSelectedTab={setSelectedTab} />
      <div className="d-flex flex-column w-100">
        <SearchHeader
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          handleExportCsv={handleExportCsv}
        />
        <UsersTable
          list={usersData?.paginatedUserList}
          totalCount={usersData?.totalUsersCount}
          isLoading={isUsersLoading}
        />
      </div>
    </UsersListContainer>
  );
};

const mapReduxStateToProps = (state: RootState) => {
  return {
    adminUserFilter: state.AdminReducer.adminUserFilter,
    currentUsersPage: state.AdminReducer.currentUsersPage
  };
};

export default connect(mapReduxStateToProps)(UsersList);
